import { z } from "zod";
import { filsSchema, isoDateTimeSchema, uuidSchema } from "./common.ts";

/**
 * No-show and late-cancel fee (Pilates studio shape, I2; migration 0078). The business chooses on
 * Packages and rules: take the session only (both fees off, the start value), or take the session
 * and ask for a fee. Nothing goes out by itself: each fee waits on the owner's To do list, and the
 * owner or an admin sends it (a one-off payment request with a card link) or waives it.
 *
 *   no_show      the person was booked and marked absent
 *   late_cancel  the member cancelled after the free-cancel time
 *
 *   to_confirm   waits for the owner
 *   sent         the owner sent it: a payment request exists
 *   waived       the owner let it go
 */
export const bookingFeeKinds = ["no_show", "late_cancel"] as const;
export const bookingFeeKindSchema = z.enum(bookingFeeKinds);
export type BookingFeeKind = z.infer<typeof bookingFeeKindSchema>;

export const bookingFeeStatuses = ["to_confirm", "sent", "waived"] as const;
export const bookingFeeStatusSchema = z.enum(bookingFeeStatuses);
export type BookingFeeStatus = z.infer<typeof bookingFeeStatusSchema>;

/** One fee that waits for the owner. */
export const feeToConfirmSchema = z.object({
  id: uuidSchema,
  kind: bookingFeeKindSchema,
  amountFils: filsSchema,
  bookingId: uuidSchema,
  participantId: uuidSchema,
  participantName: z.string(),
  /** The account holder who pays (the parent of a child, or the client). */
  memberName: z.string().nullable(),
  /** The session's name, else its class. */
  groupName: z.string(),
  sessionId: uuidSchema,
  startsAt: isoDateTimeSchema,
  /** When the fee became final (the absent mark, or the late cancel). */
  dueAt: isoDateTimeSchema,
});
export type FeeToConfirm = z.infer<typeof feeToConfirmSchema>;

/** `GET /owner/businesses/:id/fees`: the fees to confirm, the oldest first. Owner and admin only. */
export const feesToConfirmResponseSchema = z.object({
  items: z.array(feeToConfirmSchema),
  /** False when the business cannot take card payments yet: Send is refused, Waive still works. */
  canSend: z.boolean(),
});
export type FeesToConfirmResponse = z.infer<typeof feesToConfirmResponseSchema>;

/** `POST .../fees/:feeId/send` and `.../waive`. */
export const feeDecisionResponseSchema = z.object({
  id: uuidSchema,
  status: bookingFeeStatusSchema,
  /** The payment link of the request that Send made. Null for a waived fee. */
  payLink: z.string().nullable(),
  paymentRequestId: uuidSchema.nullable(),
});
export type FeeDecisionResponse = z.infer<typeof feeDecisionResponseSchema>;
